export const normalizeCashMethod = (value) => {
  const method = String(value || '').trim().toLowerCase();
  if (!method) return 'Otros';
  if (method.includes('efect')) return 'Efectivo';
  if (
    method.includes('tarjeta') || method.includes('debito') || method.includes('débito') ||
    method.includes('credito') || method.includes('crédito') || method.includes('visa') ||
    method.includes('mastercard')
  ) return 'Tarjeta';
  if (method.includes('nequi')) return 'Nequi';
  if (method.includes('daviplata')) return 'Daviplata';
  if (method.includes('bancolombia')) return 'Bancolombia';
  if (method.includes('bbva')) return 'BBVA';
  if (method.includes('davivienda')) return 'Davivienda';
  if (method.includes('rappi')) return 'Rappi';
  if (method.includes('pse')) return 'PSE';
  if (method.includes('qr')) return 'QR';
  return value || 'Otros';
};

const finiteAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

function paymentParts(sale) {
  if (Array.isArray(sale?.payments) && sale.payments.length) {
    return sale.payments
      .map((payment) => ({ method: normalizeCashMethod(payment?.method), amount: finiteAmount(payment?.amount) }))
      .filter((payment) => payment.amount > 0);
  }
  return [
    { method: normalizeCashMethod(sale?.paymentMethod), amount: finiteAmount(sale?.amountReceived) },
    { method: normalizeCashMethod(sale?.secondPaymentMethod), amount: finiteAmount(sale?.secondAmountReceived) },
  ].filter((payment) => payment.amount > 0);
}

function inferOriginalTotal(sale, parts) {
  const currentTotal = Math.max(0, Number(sale?.total || 0));
  const returnedTotal = Math.max(0, Number(sale?.returnedTotal || 0));
  const explicit = Number(sale?.originalTotal);
  if (Number.isFinite(explicit) && explicit >= currentTotal) return explicit;
  if (returnedTotal > 0) return currentTotal + returnedTotal;

  if (sale?.status === 'Devuelta' || sale?.status === 'Parcialmente Devuelta') {
    const collected = parts.reduce((sum, part) => sum + part.amount, 0);
    const change = Math.max(0, Number(sale?.change || 0));
    return Math.max(currentTotal, collected - change);
  }
  return currentTotal;
}

function addAmount(target, method, amount, countDelta = 0) {
  if (!Number.isFinite(amount) || amount === 0) return;
  const current = target[method] || { amount: 0, count: 0 };
  target[method] = {
    amount: current.amount + amount,
    count: current.count + countDelta,
  };
}

export function calculateTurnSalesAccounting(sales = []) {
  const result = {
    salesByMethod: {},
    collectionsByMethod: {},
    efectivoRecibido: 0,
    cambioEntregado: 0,
    cashRefunds: 0,
    cashFromSalesNet: 0,
    returnedTotal: 0,
  };

  (Array.isArray(sales) ? sales : []).forEach((sale) => {
    const parts = paymentParts(sale);
    parts.forEach(({ method, amount }) => {
      addAmount(result.collectionsByMethod, method, amount, 1);
    });

    const originalTotal = inferOriginalTotal(sale, parts);
    const nonCash = parts.filter((part) => part.method !== 'Efectivo');
    const cash = parts.filter((part) => part.method === 'Efectivo');
    let remaining = originalTotal;
    let cashApplied = 0;

    [...nonCash, ...cash].forEach(({ method, amount }) => {
      const applied = Math.max(0, Math.min(amount, remaining));
      addAmount(result.salesByMethod, method, applied, applied > 0 ? 1 : 0);
      remaining -= applied;
      if (method === 'Efectivo') cashApplied += applied;
    });

    const cashReceived = cash.reduce((sum, part) => sum + part.amount, 0);
    result.efectivoRecibido += cashReceived;
    result.cambioEntregado += Math.max(0, cashReceived - cashApplied);

    let refunds = Array.isArray(sale?.refunds)
      ? sale.refunds.map((refund) => ({
          method: normalizeCashMethod(refund?.method),
          amount: finiteAmount(refund?.amount),
        })).filter((refund) => refund.amount > 0)
      : [];

    if (refunds.length === 0 && (sale?.status === 'Devuelta' || sale?.status === 'Parcialmente Devuelta')) {
      const legacyRefund = Math.max(0, originalTotal - Math.max(0, Number(sale?.total || 0)));
      if (legacyRefund > 0) {
        refunds = [{ method: normalizeCashMethod(sale?.paymentMethod), amount: legacyRefund }];
      }
    }

    refunds.forEach(({ method, amount }) => {
      addAmount(result.salesByMethod, method, -amount, 0);
      result.returnedTotal += amount;
      if (method === 'Efectivo') result.cashRefunds += amount;
    });
  });

  result.cashFromSalesNet = result.efectivoRecibido - result.cambioEntregado - result.cashRefunds;
  return result;
}
